import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { useSectionTracking } from "./AnalyticsProvider";

export default function GeologicalOrigins() {
  const sectionRef = useSectionTracking("geological_origins");
  const animRef = useRef(null);
  const inView = useInView(animRef, { once: true, margin: "-60px" });

  return (
    <section ref={sectionRef} className="relative py-10 sm:py-14 lg:py-18 overflow-hidden" data-testid="geological-origins-section">
      <div className="absolute inset-0 bg-gradient-to-b from-[#111110] via-[#0D0F0B] to-[#111110]" />

      <div className="relative max-w-5xl mx-auto px-6 lg:px-12" ref={animRef}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-start">
          {/* Left: The age + why it matters */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="space-y-5"
          >
            <div>
              <p className="overline mb-3">Geological Origins</p>
              <h2 className="font-serif text-3xl sm:text-4xl text-[#F4F0EA] font-light tracking-tighter">
                Older Than <span className="italic text-[#C5A059]">Human Civilisation</span>
              </h2>
            </div>

            <div className="p-5 border border-[#C5A059]/15 bg-[#C5A059]/[0.03]">
              <p className="font-serif text-2xl sm:text-3xl text-[#C5A059]/70 mb-1">30 – 65 Million Years Old</p>
              <p className="text-sm text-[#B8B3AA]/70">This crystal predates the entire history of the human species.</p>
            </div>

            <p className="text-base text-[#B8B3AA]/75 leading-relaxed">
              Colombian emeralds formed through <span className="text-[#C5A059]/70 italic">sedimentary hydrothermal processes</span> — a mechanism fundamentally different from emeralds in Zambia, Brazil, or Afghanistan. This geologically rare convergence of tectonic uplift, extreme pressure, and chromium meeting beryllium-rich fluids is what gives Muzo emeralds their unmatched colour and clarity.
            </p>

            <p className="text-sm text-[#B8B3AA]/65 leading-relaxed">
              The conditions that created this crystal no longer exist. The geological window has closed.
            </p>
          </motion.div>

          {/* Right: Timeline */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:pt-16"
          >
            <div className="relative pl-6 sm:pl-10 ml-3 border-l border-[#C5A059]/15">
              {[
                {
                  time: "30–65 Million Years Ago",
                  text: "Hydrothermal fluids carrying chromium interact with beryllium-rich sedimentary rock under immense heat and pressure. A crystal begins to grow.",
                },
                {
                  time: "Tens of Millions of Years",
                  text: "Tectonic forces push the Andes skyward. Most emerald formations are fractured, crushed, destroyed. This crystal survives intact.",
                },
                {
                  time: "Modern Discovery",
                  text: "Recovered from the Muzo region of Colombia — crystal structure, transparency, and vivid coloration preserved across geological time.",
                },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 15 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.4 + i * 0.15 }}
                  className="relative pb-8 last:pb-0"
                >
                  <div className="absolute -left-[7px] top-1.5 w-[8px] h-[8px] border border-[#C5A059]/40 bg-[#0D0F0B] rounded-full" />
                  <p className="text-xs text-[#C5A059]/60 uppercase tracking-[0.15em] mb-1.5">{item.time}</p>
                  <p className="text-sm text-[#B8B3AA]/70 leading-relaxed">{item.text}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
